import { formatEur, type MemberDto } from "@gladis/shared";
import { firstNameOf, StatCard, StatRow } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { expenseScopeOf, type ExpenseFilterState } from "./ExpenseFilters.js";
import { useExpensesChart } from "./ExpensesChart.js";
import type { ExpensesPageDto } from "./ExpenseList.js";

/**
 * The row of figures the Expenses page opens with.
 *
 * ==========================================================================================
 * FOUR CARDS, AND EACH ANSWERS A QUESTION THE OTHERS CANNOT
 * ==========================================================================================
 *
 * **Per month** — what the household is committed to. The one number the page exists for.
 * **This month vs last** — whether that is moving, which the total alone cannot say.
 * **Who pays what** — the same total decomposed by whose cost it is, from the splits.
 * **Settling up** — what has to move between accounts to make those splits true.
 *
 * The last two are the pair worth understanding: the third says what each member is RESPONSIBLE for, the
 * fourth says what each member must TRANSFER, and the difference between them is which card each cost leaves
 * from. A member funding a cost they hold 100% of appears in full in the third card and not at all in the
 * fourth.
 *
 * ==========================================================================================
 * IT IS GIVEN THE PAYLOAD, AND IT IS TOLD THE SCOPE
 * ==========================================================================================
 *
 * `ExpenseList` reads `useExpenses(filters)` for itself; this is handed the answer, so the page costs one
 * request. But it DOES take the filters, and that is a correction: it used to take only the payload, on the
 * argument that every figure here covered the household whatever the filter said, and that a component
 * accepting filters in order to ignore them invites the next reader to wonder which ones it honoured.
 *
 * The argument held for three of the four cards and never for the second. "This month vs last" is the last
 * step of the TREND, and it fetched the trend UNFILTERED while the chart directly below it drew a filtered
 * line — one card and one picture of the same series, disagreeing, with nothing on either to say why. Now
 * the whole row follows the filter: the first card is the scope's own commitment, the second is the step in
 * the scope's own line, the third is dropped under a member scope because it would be the first card again,
 * and the fourth says out loud that it is the one figure a scope cannot touch.
 *
 * The trend is fetched through the same hook and cache key `ExpensesChart` uses, WITH THE SAME FILTERS — so
 * the "vs last month" figure and the line below it cannot disagree, and the two components still cost one
 * request.
 *
 * ==========================================================================================
 * NOTHING HERE COMPUTES A TOTAL
 * ==========================================================================================
 *
 * Every euro on screen arrives already summed in integer cents by the server. The only arithmetic in this file
 * is one subtraction between two figures the chart endpoint returned — deliberately not a sum over the rows,
 * which would be a second implementation of the product's rounding rule sitting next to the first.
 */
export function ExpenseStats({
  page,
  filters,
  householdMembers = [],
}: {
  page: ExpensesPageDto | undefined;
  /**
   * The page's scope, for the trend fetch and for what each card says about itself.
   *
   * NOT for filtering anything here: every figure arrives already scoped by the server, through the one
   * rule in `scoped-share.ts`. What this decides is which SENTENCE goes under each figure, and whether
   * the "Who pays what" card is drawn at all.
   */
  filters?: ExpenseFilterState;
  /**
   * The household's members, for their faces (020).
   *
   * NAMED `householdMembers` and not `members`, because `members` is already taken inside this
   * component for the by-member breakdown rows, which are a different thing: those are shares of a
   * cost, including for people who have left, while these are the current member list the faces
   * come from. Two lists that overlap but are not the same, and one of them keeps departed people.
   */
  householdMembers?: readonly MemberDto[];
}): ReactNode {
  const t = useT();
  /*
   * The trend, for the month-on-month step. Loaded independently of the page payload, so a slow or failed
   * chart read costs the other three cards nothing — this one is simply absent, which is the same thing it
   * does for a household that has recorded nothing.
   *
   * WITH THE FILTERS. Unfiltered, this card reported the household's step above a chart drawing somebody
   * else's line, and the same cache key means the two are one request rather than two answers.
   */
  const chart = useExpensesChart(filters);
  const scope = expenseScopeOf(filters, householdMembers);

  if (!page) return null;

  const points = chart.data?.points ?? [];
  const latest = points.at(-1);
  const previous = points.at(-2);
  /*
   * A step needs two months that both exist. A household in its first month has one point and nothing to
   * compare against, and "+ € 2.693,11 since nothing" is a statement about when they started using GLADIS
   * rather than about their spending.
   */
  const step = latest && previous ? latest.totalInEuros - previous.totalInEuros : null;

  const members = page.byMember;

  return (
    <StatRow>
      <StatCard
        label={t("exp.list.colPerMonth")}
        value={formatEur(page.monthlyTotalInEuros)}
        testId="stat-monthly-total"
        hint={
          page.mortgageUnavailable ? (
            /*
             * The one case where this card must say something is wrong. A failed properties read leaves the
             * mortgage out of the figure above with nothing else on the page to reveal it — the rows are
             * simply absent, which looks exactly like a household without a mortgage.
             *
             * IT WINS OVER THE SCOPE SENTENCE. A figure that is short is a worse thing not to know than
             * whose figure it is, and the scope is named in the list's own sentence a section below.
             */
            <>{t("exp.stats.mortgageShort")}</>
          ) : scope.kind === "member" ? (
            <>{t("exp.stats.perMonthHintMember", { name: scope.name })}</>
          ) : scope.kind === "joint" ? (
            <>{t("exp.stats.perMonthHintJoint")}</>
          ) : (
            <>{t("exp.stats.perMonthHint")}</>
          )
        }
      />

      {step === null || latest === undefined || previous === undefined ? null : (
        <StatCard
          label={t("exp.stats.thisMonthVsLast")}
          // Signed always, including the zero: "€ 0,00" with a level arrow is a statement, and dropping the
          // sign would make an increase and a decrease look the same at a glance.
          value={`${step > 0 ? "+" : step < 0 ? "−" : ""} ${formatEur(Math.abs(step))}`}
          trend={step > 0 ? "up" : step < 0 ? "down" : "level"}
          testId="stat-monthly-change"
          hint={<>{formatEur(previous.totalInEuros)} the month before.</>}
        />
      )}

      {/*
        ONE MEMBER MEANS NO CARD. Their share of the household's costs is the household's costs, so the card
        would be the first one again under a different name.

        AND A MEMBER SCOPE IS THE SAME SITUATION, reached from the other direction: the server returns one
        row there, holding the figure the first card already prints. `joint` keeps the card, because an
        equal share of the unsplit set is a genuine decomposition of it rather than the headline restated.
      */}
      {members.length > 1 && scope.kind !== "member" ? (
        <StatCard
          label={t("exp.stats.whoPaysWhat")}
          testId="stat-by-member"
          rows={members.map((member) => ({
            key: member.memberId,
            // First name beside the face, like every other person-row in the application. "(left)"
            // stays because it is not part of the name — it is the row's status.
            label: member.isFrozen
              ? `${firstNameOf(member.displayName)} (left)`
              : firstNameOf(member.displayName),
            value: formatEur(member.monthlyInEuros),
            ...(member.isFrozen ? { muted: true } : {}),
            /*
              A frozen row still names a person — the split snapshots `member_display_name` — so it
              keeps initials while the lookup misses. `avatarName` is passed explicitly because the
              label reads "Alice (left)", and initials drawn from that would be "A(".
            */
            avatarName: member.displayName,
            avatarUrl:
              householdMembers.find((m) => m.userId === member.memberId)?.avatarUrl ?? null,
          }))}
          hint={<>{t("exp.stats.whoPaysWhatHint")}</>}
        />
      ) : null}

      {/*
        RENDERED ONLY WHEN THERE IS SOMETHING TO SETTLE (FR-066), which is the rule the panel this replaced
        followed: a household with one member, or with no card-bound expenses, has nothing to compute, and a
        card reading "€ 0,00" would claim the settle-up had been computed and come to nothing instead.

        It carries `data-testid="settle-up"`, which the e2e suite scopes its assertions to. A name-based
        locator would match the sentence "Anouk owes Damien" anywhere on the page, including in this card's
        neighbour.
      */}
      {page.settleUp.length > 0 ? (
        <StatCard
          label={t("exp.stats.settlingUp")}
          testId="settle-up"
          {...(page.settleUp.length === 1 && page.settleUp[0]
            ? {
                value: formatEur(page.settleUp[0].amountInEuros),
                hint: (
                  <>
                    {page.settleUp[0].debtorName} owes {page.settleUp[0].creditorName}, every month.{" "}
                    {/* Whole-household, and it says so only while a scope is on — see the multi-row
                        branch below, which carries the same pair of sentences. */}
                    {scope.kind === "all"
                      ? t("exp.stats.settleUpNote")
                      : t("exp.stats.settlingUpHintWholeHousehold")}
                  </>
                ),
              }
            : {
                rows: page.settleUp.map((line) => ({
                  key: `${line.debtorId}-${line.creditorId}`,
                  // Phrased directionally (FR-065). The server has already netted each pair and dropped the
                  // ones that come to zero, so there is no negative amount to render.
                  // Both names shortened, so the sentence stays a sentence: "Ada owes Jean".
                  label: `${firstNameOf(line.debtorName)} owes ${firstNameOf(line.creditorName)}`,
                  value: formatEur(line.amountInEuros),
                  /*
                    THE DEBTOR'S FACE, not both. A settle-up row is about a PAIR, and two avatars in
                    a stat row is a picture of a transaction rather than a figure — the card's job
                    is a number and a name. The debtor is the one being asked to act, so theirs is
                    the face that belongs beside the sentence.
                  */
                  avatarName: line.debtorName,
                  avatarUrl:
                    householdMembers.find((m) => m.userId === line.debtorId)?.avatarUrl ?? null,
                })),
                hint: (
                  <>
                    {scope.kind === "all"
                      ? t("exp.stats.settlingUpHint")
                      : t("exp.stats.settlingUpHintWholeHousehold")}
                  </>
                ),
              })}
        />
      ) : null}
    </StatRow>
  );
}
